import React, { useEffect, useState, useCallback } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiAlertTriangle, FiCheckCircle, FiSearch, FiChevronLeft, FiChevronRight, FiLink } from 'react-icons/fi';
import { AdminSidebar } from '../../components/admin';
import { Modal } from '../../components/ui';

const PAGE_SIZE = 5;
const API_BASE = 'https://www.s2i.com.tn';

const getFullImageUrl = (path, name) => {
  if (!path) return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Partner')}&background=f1f5f9&color=d90000&size=128&bold=true`;
  if (path.startsWith('http')) return path;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${cleanPath}`;
};

const handleImageError = (e, name) => {
  e.target.onerror = null; 
  e.target.src = `https://via.placeholder.com/150/f1f5f9/d90000?text=${encodeURIComponent(name || 'Partner')}`;
};

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedPartner, setSelectedPartner] = useState(null);
  
  // ✅ État mis à jour pour le DTO (logo_url, website_url)
  const [form, setForm] = useState({ name: '', description: '', logo_url: '', website_url: '' });
  
  const [notification, setNotification] = useState({ show: false, type: '', msg: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const token = localStorage.getItem('token');

  const fetchPartners = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const res = await fetch(`${API_BASE}/api/partners?page=${page}&size=${PAGE_SIZE}&sortBy=id&direction=desc${searchParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPartners(data.content || []);
      setTotalPages(data.totalPages || 1);
      setTotalElements(data.totalElements || 0);
    } catch {
      showNotify('error', 'Erreur de chargement des partenaires');
    }
    setLoading(false);
  }, [searchTerm, token]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      fetchPartners(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, fetchPartners]);

  useEffect(() => {
    fetchPartners(currentPage - 1);
  }, [currentPage, fetchPartners]);

  const showNotify = (type, msg) => {
    setNotification({ show: true, type, msg });
    setTimeout(() => setNotification({ show: false, type: '', msg: '' }), 3000);
  };

  const handleShowForm = (type, partner = null) => {
    setModalType(type);
    setSelectedPartner(partner);
    setForm(partner ? { 
        name: partner.name, 
        description: partner.description || '', 
        logo_url: partner.logo || '', 
        website_url: partner.websiteUrl || '' 
    } : { name: '', description: '', logo_url: '', website_url: '' });
    setShowFormModal(true);
  };

  const handleShowDelete = (partner) => {
    setSelectedPartner(partner);
    setShowDeleteModal(true);
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', 'partners');

    try {
      const res = await fetch(`${API_BASE}/api/files/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const path = await res.text();
      setForm(prev => ({ ...prev, logo_url: path }));
      showNotify('success', 'Image partenaire téléchargée !');
    } catch {
      showNotify('error', "Échec de l'upload de l'image");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setModalLoading(true);
    try {
      const method = modalType === 'add' ? 'POST' : 'PUT';
      const url = modalType === 'add' ? `${API_BASE}/api/partners` : `${API_BASE}/api/partners/${selectedPartner.id}`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });

      if (!res.ok) throw new Error();
      showNotify('success', modalType === 'add' ? 'Partenaire ajouté !' : 'Partenaire mis à jour !');
      fetchPartners(currentPage - 1);
      setShowFormModal(false);
    } catch {
      showNotify('error', "Erreur lors de l'enregistrement");
    } finally {
      setModalLoading(false);
    }
  };

  const executeDelete = async () => {
    setModalLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/partners/${selectedPartner.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      showNotify('success', 'Partenaire supprimé !');
      if (partners.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      } else {
        fetchPartners(currentPage - 1);
      }
      setShowDeleteModal(false);
    } catch {
      showNotify('error', 'Erreur lors de la suppression');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-header">
            <div>
                <h2>Gestion des Partenaires</h2>
                <p className="text-muted small">Total : {totalElements} enregistrés</p>
            </div>
            <div className="search-wrapper">
                <FiSearch className="search-icon" />
                <input type="text" placeholder="Rechercher..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <button onClick={() => handleShowForm('add')} className="btn-add"><FiPlus /> Nouveau Partenaire</button>
        </div>

        {notification.show && (
            <div className={`toast-sii ${notification.type}`}>
                {notification.type === 'success' ? <FiCheckCircle /> : <FiAlertTriangle />}
                {notification.msg}
            </div>
        )}

        {loading ? (
          <div className="loader-container"><div className="loader"></div></div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr><th>Logo</th><th>Nom</th><th>Lien Web</th><th className="text-end">Actions</th></tr>
              </thead>
              <tbody>
                {partners.length > 0 ? partners.map(p => (
                  <tr key={p.id}>
                    <td><img src={getFullImageUrl(p.logo, p.name)} alt="logo" className="admin-logo-img" onError={(e) => handleImageError(e, p.name)}/></td>
                    <td className="fw-bold">{p.name}</td>
                    <td>
                        {p.websiteUrl ? (
                            <a href={p.websiteUrl} target="_blank" rel="noreferrer" className="site-link"><FiLink /> Lien Site</a>
                        ) : <span className="text-muted small">-</span>}
                    </td>
                    <td className="text-end">
                      <button onClick={() => handleShowForm('edit', p)} className="btn-icon edit"><FiEdit /></button>
                      <button onClick={() => handleShowDelete(p)} className="btn-icon delete"><FiTrash2 /></button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="4" className="text-center py-4">Aucun partenaire trouvé.</td></tr>
                )}
              </tbody>
            </table>

            <div className="pagination">
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><FiChevronLeft /> Précédent</button>
                <span>Page {currentPage} sur {totalPages}</span>
                <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Suivant <FiChevronRight /></button>
            </div>
          </>
        )}

        <Modal isOpen={showFormModal} onClose={() => setShowFormModal(false)} title={modalType === 'add' ? 'Ajouter Partenaire' : 'Modifier Partenaire'}>
          <form onSubmit={handleSubmit} className="admin-form">
            <label className="small fw-bold">Nom</label>
            <input name="name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nom du partenaire" required className="form-input"/>
            
            <label className="small fw-bold">Description</label>
            <textarea name="description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description..." className="form-input" rows="2"/>
            
            <label className="small fw-bold">Site Web (Lien)</label>
            <input name="website_url" value={form.website_url} onChange={e => setForm({...form, website_url: e.target.value})} placeholder="https://..." className="form-input"/>

            <div className="upload-box">
                <label className="small fw-bold">Logo :</label>
                <input type="file" onChange={handleFileChange} accept="image/*" />
            </div>
            {form.logo_url && (
              <div className="preview-container">
                <img src={getFullImageUrl(form.logo_url, form.name)} alt="prev" className="preview-img" onError={(e) => handleImageError(e, form.name)}/>
              </div>
            )}
            <button type="submit" className="btn-save" disabled={modalLoading || uploadLoading}>{modalLoading ? 'Sauvegarde...' : 'Confirmer'}</button>
          </form>
        </Modal>

        <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirmation">
            <div className="text-center p-3">
                <FiAlertTriangle size={50} color="#ef4444" />
                <h4 className="mt-3">Suppression ?</h4>
                <p>Voulez-vous supprimer <b>{selectedPartner?.name}</b> ?</p>
                <div className="d-flex justify-content-center gap-3 mt-4">
                    <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Annuler</button>
                    <button className="btn-confirm-delete" onClick={executeDelete} disabled={modalLoading}>Oui, supprimer</button>
                </div>
            </div>
        </Modal>
      </div>
      <style>{`
        .admin-container { display:flex; min-height:100vh; background:#f4f7fe; }
        .admin-content { flex:1; padding:40px; position:relative; }
        .admin-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:30px; gap: 20px; }
        .btn-add { background:#d90000; color:white; border:none; padding:12px 24px; border-radius:10px; cursor:pointer; font-weight:600; display:flex; align-items:center; gap:8px; }
        .admin-table { width:100%; background:white; border-radius:12px; border-collapse:collapse; box-shadow:0 4px 12px rgba(0,0,0,0.05); }
        .admin-table td, .admin-table th { padding:15px; border-bottom:1px solid #f1f5f9; }
        .admin-logo-img { height:40px; width:80px; object-fit:contain; border-radius:6px; background:#f8fafc; }
        .site-link { color: #d90000; text-decoration: none; font-size: 0.85rem; font-weight: 600; display: flex; align-items: center; gap: 4px; }
        .btn-icon { background:none; border:none; cursor:pointer; font-size:18px; margin-left:10px; padding:8px; border-radius:8px; }
        .edit { color:#3b82f6; } .delete { color:#ef4444; }
        .admin-form { display:flex; flex-direction:column; gap:8px; }
        .form-input { padding:10px; border:1px solid #e2e8f0; border-radius:8px; outline:none; }
        .preview-img { height:60px; object-fit:contain; }
        .btn-save { background:#d90000; color:white; border:none; padding:12px; border-radius:10px; cursor:pointer; font-weight:700; margin-top:10px; }
        .pagination { display:flex; justify-content:center; align-items:center; gap:20px; margin-top:30px; }
        .pagination button { padding:8px 16px; border-radius:8px; border:1px solid #ddd; background:white; cursor:pointer; }
        .search-wrapper { position: relative; flex: 1; max-width: 400px; }
        .search-icon { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
        .search-input { width: 100%; padding: 10px 10px 10px 45px; border-radius: 25px; border: 1px solid #e2e8f0; outline: none; }
        .loader { width:40px; height:40px; border:4px solid #f1f5f9; border-top:4px solid #d90000; border-radius:50%; animation:spin 1s linear infinite; }
        @keyframes spin { to{transform:rotate(360deg)} }
        .loader-container { display:flex; justify-content:center; padding:100px; }
      `}</style>
    </div>
  );
};

export default AdminPartners;